import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function Services() {
  const services = [
    {
      icon: 'ri-plant-line',
      title: 'Cocoa Sourcing & Export',
      description: 'Working directly with farmers to deliver top-grade, sun-dried, machine-finished, and hand-selected beans.',
      features: [
        'Direct farmer partnerships',
        'Premium quality beans',
        'Sun-dried processing',
        'Machine-finished selection',
        'Hand-sorted quality control',
        'Export documentation'
      ],
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/cbd83e2a12bf139ba03dbce5cc212094.png'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Traceability & Certification',
      description: 'GPS farm mapping, farmer ID systems, and monitoring tools for global compliance.',
      features: [
        'GPS farm mapping',
        'Digital farmer ID systems',
        'Real-time monitoring',
        'EUDR compliance',
        'Rainforest Alliance certification',
        'Supply chain transparency'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20agricultural%20technology%20in%20Nigerian%20cocoa%20farms%2C%20GPS%20mapping%20equipment%2C%20digital%20tracking%20systems%2C%20farmers%20using%20tablets%20and%20smartphones%2C%20sustainable%20farming%20certification%20process%2C%20high-tech%20agriculture%20meets%20traditional%20farming%2C%20professional%20documentation%20photography&width=600&height=400&seq=traceability-tech&orientation=landscape'
    },
    {
      icon: 'ri-building-line',
      title: 'Bonded Warehousing & Logistics',
      description: 'Secure bonded facilities and an inland dry port in Akure for smooth export operations.',
      features: [
        'Secure bonded warehouses',
        'Climate-controlled storage',
        'Inland dry port facilities',
        'Customs clearance',
        'Quality preservation',
        'Export logistics coordination'
      ],
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/82058ae077668011e0e36c4ba6b38fce.jpeg'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Farmer Engagement & Capacity Building',
      description: 'Training in GAP, sustainability, post-harvest handling, and rehabilitation programs.',
      features: [
        'Good Agricultural Practices training',
        'Sustainability education',
        'Post-harvest handling',
        'Farm rehabilitation support',
        'Financial literacy programs',
        'Community development'
      ],
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/79c2e56dd919e60712c23a6448d667c6.jpeg'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Farm Selection',
      description: 'Identify and partner with qualified farmers in prime cocoa-growing regions'
    },
    {
      step: '02',
      title: 'Quality Assessment',
      description: 'Rigorous quality control and grading of cocoa beans at farm level'
    },
    {
      step: '03',
      title: 'Processing & Storage',
      description: 'Professional processing, drying, and climate-controlled storage'
    },
    {
      step: '04',
      title: 'Certification & Documentation',
      description: 'Complete traceability documentation and certification processes'
    },
    {
      step: '05',
      title: 'Export & Delivery',
      description: 'Efficient logistics and timely delivery to global destinations'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/8333344beb35f27af3732e67fff42ebf.png')`
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our Services
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
            Comprehensive cocoa solutions from farm to global markets. 
            We provide end-to-end services that ensure quality, sustainability, and compliance.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-2xl text-amber-600`}></i>
                  </div>
                  <h2 className="text-3xl font-bold text-green-800 mb-6">{service.title}</h2>
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">{service.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-check-line text-green-600"></i>
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button variant="primary">
                    Learn More
                    <div className="w-5 h-5 flex items-center justify-center ml-2">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </Button>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-96 object-cover object-top rounded-2xl shadow-lg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From farm to export, our streamlined process ensures quality, 
              sustainability, and compliance at every step.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">{step.step}</span>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-green-200 transform translate-x-4"></div>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-green-800 mb-3">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-green-800 mb-6">
                Uncompromising Quality Standards
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Our commitment to quality begins at the farm level and extends through every 
                  stage of processing and export. We maintain rigorous quality control standards 
                  that exceed international requirements.
                </p>
                <p>
                  Every batch of cocoa beans undergoes comprehensive testing for moisture content, 
                  bean size, fermentation quality, and freedom from defects. Our state-of-the-art 
                  facilities ensure optimal storage conditions that preserve the natural flavors 
                  and characteristics of Nigerian cocoa.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="bg-green-50 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-award-line text-xl text-green-600"></i>
                  </div>
                  <h4 className="font-semibold text-green-800 mb-2">Premium Grade</h4>
                  <p className="text-sm text-gray-600">Grade 1 cocoa beans meeting international standards</p>
                </div>
                
                <div className="bg-amber-50 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-shield-check-line text-xl text-amber-600"></i>
                  </div>
                  <h4 className="font-semibold text-green-800 mb-2">Certified Quality</h4>
                  <p className="text-sm text-gray-600">Rainforest Alliance and EUDR compliant</p>
                </div>
              </div>
            </div>
            
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=African%20Nigerian%20quality%20control%20scientists%20and%20laboratory%20technicians%20testing%20cocoa%20beans%2C%20Black%20professionals%20in%20white%20lab%20coats%20conducting%20detailed%20analysis%2C%20diverse%20team%20of%20African%20researchers%20examining%20cocoa%20samples%20under%20microscopes%2C%20modern%20laboratory%20setting%20with%20Nigerian%20experts%2C%20precision%20quality%20testing%20by%20Black%20scientists%20and%20technicians%2C%20professional%20scientific%20photography%20with%20excellent%20lighting&width=600&height=500&seq=african-lab-professionals&orientation=portrait"
                alt="Quality Control"
                className="w-full h-96 object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Experience Our Services?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Partner with us for reliable, sustainable, and high-quality cocoa sourcing. 
            Contact our team to discuss your specific requirements.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button variant="primary" size="lg" className="bg-amber-600 hover:bg-amber-700">
              Request Quote
              <div className="w-5 h-5 flex items-center justify-center ml-2">
                <i className="ri-mail-line"></i>
              </div>
            </Button>
            <Link to="/why-choose-us">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-green-800">
                Why Choose Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
